import { useEffect, useContext, useRef, useState } from 'react'

import { AuthContext } from '../../../context/AuthContext'

import { setSessionInUser, updateUser } from '../../../service/userService'
import { joinSession, joinSessionResult, IncrementStuNumInSession } from '../../../service/sessionService'

// updateUser/joinSession both swallow their own network errors internally and resolve to
// a falsy value instead of throwing (see their definitions in userService.js/
// sessionService.js) - this function previously never checked either result, so a
// transient failure under load (a slow DB, a dropped connection - exactly what a whole
// class joining within the same minute can cause) would silently leave the student's
// session_id/session_list and their user_session row unset, while local React state (and
// the rest of the UI) proceeded as if the join had succeeded. A real class deployment hit
// this: several students generated real activity (chat messages, telemetry) under a
// user_id that never got a user_session row for that session. Retrying with backoff here,
// and surfacing joinFailed when it still doesn't work, closes that silent-failure gap.
const MAX_ATTEMPTS = 3
const RETRY_DELAY_MS = [500, 1500, 3000]
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// Sessions whose join failed in this page load. Kept outside the component: the
// setCurrentUser below remounts WorkspacePage (App.js defines ProtectedRoute inside App), and
// the remounted hook would otherwise start with joinFailed false and lose the banner.
const failedJoins = new Set()

// `enabled`: false while the page is still working out whether this session is the
// student's to join (an experiment's condition, see useExperimentEntry).
export const useSessionJoin = (sessionId, { enabled = true } = {}) => {
  const { currentUser, setCurrentUser } = useContext(AuthContext)

  const hasAttemptedJoin = useRef(false)
  const [joinFailed, setJoinFailed] = useState(() => failedJoins.has(Number(sessionId)))

  useEffect(() => {
    // Wait for both ID and User to be ready
    if (!sessionId || !currentUser || !enabled) return

    // Same rule as Home.jsx: an instructor (role 1) must not take the student join
    // path below (session_list, stu_num, user_session). WorkspacePage stays on
    // "Loading Session..." until SessionContext's session.id matches this route, and
    // SessionContext only follows currentUser.session_id. Point that at this workspace
    // and skip the rest.
    if (currentUser.role === 1) {
      if (String(currentUser.session_id) === String(sessionId)) return
      const nextSessionId = Number(sessionId)
      // An instructor-table sign-in keeps it on its own row (see Home.jsx).
      const save = currentUser.kind === 'instructor'
        ? setSessionInUser(currentUser.id, nextSessionId)
        : updateUser({ id: currentUser.id, session_id: nextSessionId })
      save.then((ok) => {
        if (ok) setCurrentUser({ ...currentUser, session_id: nextSessionId })
      })
      return
    }

    // Stop if we already ran this logic for this page load
    if (hasAttemptedJoin.current) return

    const syncUserToSession = async () => {
      hasAttemptedJoin.current = true
      setJoinFailed(failedJoins.has(Number(sessionId)))

      // If user.session_id already matches, the DB already has this join recorded (that's
      // the definition of this branch) - the backend's own join endpoint actually rejects
      // a repeat call for an already-registered user with a 400 ("User already registered
      // in this session"), which is an expected no-op here, not a real failure. Retrying
      // and surfacing joinFailed on that response (an earlier version of this fix did
      // exactly that) would show the "trouble saving your join" banner on every single
      // page reload/revisit for a student who is, in fact, already correctly joined - a
      // regression discovered by testing this fix live. Fire-and-forget, matching what
      // this branch already trusts from user.session_id.
      // Compared as numbers: /session/:id stores session_id as the route's string, this
      // page passes a number, so === sent every revisit down the full join path below.
      if (Number(currentUser.session_id) === Number(sessionId)) {
        joinSession(sessionId, currentUser.id, currentUser.uid)
        return
      }

      // Create a copy of the user to update
      const updatedUser = { ...currentUser }

      // Reset state for new session
      updatedUser.group_id = null
      updatedUser.code_id = null
      updatedUser.session_id = sessionId

      // Add to history list if they haven't been here before. session_list is
      // ARRAY(INTEGER) in Postgres but sessionId is a route param (string) -
      // compare/push the numeric form or .includes() always misses and this
      // re-increments stu_num on every visit, not just the real first join.
      const isFirstVisit = !updatedUser.session_list?.includes(Number(sessionId))
      if (isFirstVisit) {
        updatedUser.session_list = updatedUser.session_list || []
        updatedUser.session_list.push(Number(sessionId))
      }

      let success = false
      let incremented = false
      for (let attempt = 0; attempt < MAX_ATTEMPTS && !success; attempt++) {
        if (attempt > 0) await wait(RETRY_DELAY_MS[attempt - 1])

        // 1. Update Backend
        const updateOk = await updateUser(updatedUser)
        if (!updateOk) continue

        // Only increment the first-visit counter once total, on whichever attempt first
        // gets the user record itself saved - not once per retry.
        if (isFirstVisit && !incremented) {
          await IncrementStuNumInSession(sessionId)
          incremented = true
        }

        // 2. Join (writes the live user_session row). A student the backend already has in
        // this session (joined earlier, e.g. on /session/:id before Python moved here) and a
        // guest (no uid, no row to write) are joined, not failures - joinSession reports
        // both as false, which used to show the "trouble saving your join" banner.
        success = (await joinSessionResult(sessionId, updatedUser.id, updatedUser.uid)) !== 'failed'
      }

      if (success) {
        // 3. Update Frontend Context - only once the backend actually has it
        failedJoins.delete(Number(sessionId))
        setCurrentUser(updatedUser)
      } else {
        console.error('Failed to join session after retries:', sessionId)
        failedJoins.add(Number(sessionId))
        setJoinFailed(true)
        // Still move this tab to the route's session, as /session/:id always did:
        // SessionContext follows currentUser.session_id, and without this the page waits on
        // "Loading Session..." for good, where the joinFailed banner can't show. The banner
        // asks the student to refresh, which retries the join. This remounts the page; the
        // remounted hook reads failedJoins, so the banner stays.
        setCurrentUser(updatedUser)
      }
    }

    syncUserToSession()
  }, [sessionId, currentUser, setCurrentUser, enabled])

  return { joinFailed }
}
